import { CommonModule } from '@angular/common'
import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  inject,
  Injector,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core'
import { MatDialog, MatDialogConfig } from '@angular/material/dialog'
import { MatMenuTrigger } from '@angular/material/menu'
import { Router } from '@angular/router'
import { Store } from '@ngrx/store'
import {
  initializeModelAdapter,
  NgDiagramBackgroundComponent,
  NgDiagramComponent,
  NgDiagramConfig,
  NgDiagramModelService,
  NgDiagramNodeTemplateMap,
  NgDiagramSelectionService,
  NgDiagramService,
  provideNgDiagram,
} from 'ng-diagram'
import { Observable, Subscription } from 'rxjs'
import { AngularMaterialsModules } from 'src/app/module/materials.module'
import { AppState } from 'src/app/ngrx/states'
import { getTheme } from 'src/app/ngrx/store/selectors'
import { FigureService } from 'src/app/service/data/figure.service'
import { NoteService } from 'src/app/service/data/note.service'
import { PairService } from 'src/app/service/data/pair.service'
import { QuizService } from 'src/app/service/data/quiz.service'
import { WordService } from 'src/app/service/data/word.service'
import { UserService } from 'src/app/service/user-info/user.service'
import { DdcDesktopDialogComponent } from 'src/app/shared/ddc/ddc-desktop-dialog/ddc-desktop-dialog.component'
import { DdcMobileSheetComponent } from 'src/app/shared/ddc/ddc-mobile-sheet/ddc-mobile-sheet.component'
import {
  AddCanvasElementResult,
  AddCanvasElementsDialogComponent,
} from '../add-canvas-elements-dialog/add-canvas-elements-dialog.component'
import { CanvasData, CanvasNodeType, TraceNodeResult } from '../models/canvas.model'
import {
  NodeConfigDialogData,
  NodeConfigDialogResult,
} from '../node-config-dialog/node-config-dialog.component'
import { CanvasPlaceholderComponent } from './canvas-placeholder/canvas-placeholder.component'
import { CanvasTraceStateService } from './canvas-trace-state.service'
import { HistoryModelAdapter } from './history-model-adapter'
import { CanvasMinimapPanelComponent } from './minimap-panel/minimap-panel.component'
import {
  CanvasAttachmentNodeComponent,
  CanvasCreateNodeComponent,
  CanvasDictionaryNodeComponent,
  CanvasFigureNodeComponent,
  CanvasNoteNodeComponent,
  CanvasPairNodeComponent,
  CanvasQuizNodeComponent,
  CanvasWordNodeComponent,
} from './node-templates'

@Component({
  selector: 'ddc-canvas-diagram',
  templateUrl: './ddc-canvas-diagram.component.html',
  styleUrls: ['./ddc-canvas-diagram.component.scss'],
  imports: [
    CommonModule,
    AngularMaterialsModules,
    NgDiagramComponent,
    NgDiagramBackgroundComponent,
    CanvasPlaceholderComponent,
    CanvasMinimapPanelComponent,
  ],
  providers: [provideNgDiagram(), CanvasTraceStateService],
  standalone: true,
})
export class DdcCanvasDiagramComponent implements OnChanges, OnDestroy, OnInit {
  private injector = inject(Injector)
  private modelService = inject(NgDiagramModelService)
  private ngDiagramService = inject(NgDiagramService)
  private selectionService = inject(NgDiagramSelectionService)
  private dialog = inject(MatDialog)
  private wordService = inject(WordService)
  private pairService = inject(PairService)
  private noteService = inject(NoteService)
  private figureService = inject(FigureService)
  private quizService = inject(QuizService)
  private store = inject<Store<AppState>>(Store)
  private el = inject(ElementRef)
  private traceStateService = inject(CanvasTraceStateService)
  private router = inject(Router)

  private _theme$: Observable<string>
  private _isMobile = false
  private showContentSub: Subscription | null = null

  @Input() canvasData: CanvasData | null = null
  @Input() nodeResults: Record<string, TraceNodeResult> = {}
  @Input() isRunning: boolean = false
  @Input() canRun: boolean = false
  @Input() isTraceMode: boolean = false
  @Output() diagramChanged = new EventEmitter<void>()
  @Output() modelReady = new EventEmitter<void>()
  @Output() runCanvas = new EventEmitter<void>()

  @ViewChild('diagramMenuTrigger') contextMenuTrigger: MatMenuTrigger
  @ViewChild('nodeMenuTrigger') nodeMenuTrigger: MatMenuTrigger

  contextMenuPosition = { x: 0, y: 0 }
  contextNodeType: CanvasNodeType | null = null
  contextNodeContentId: number | null = null
  private contextNodeId: string | null = null
  private nodeCounter = 0

  readonly diagramConfig: NgDiagramConfig = {
    hideWatermark: true,
    viewportPanningEnabled: false,
  }

  isPanning = false

  model = initializeModelAdapter(new HistoryModelAdapter())

  nodeTemplates = new NgDiagramNodeTemplateMap([
    ['word', CanvasWordNodeComponent],
    ['pair', CanvasPairNodeComponent],
    ['dictionary', CanvasDictionaryNodeComponent],
    ['note', CanvasNoteNodeComponent],
    ['quiz', CanvasQuizNodeComponent],
    ['figure', CanvasFigureNodeComponent],
    ['create', CanvasCreateNodeComponent],
    ['attachment', CanvasAttachmentNodeComponent],
  ])

  private isEngineReady = false
  private isLoadingModel = false
  private eventUnsubscribers: (() => void)[] = []
  private historyAdapter: HistoryModelAdapter | null = null

  private _username: string = ''

  constructor(private user_srv: UserService) {
    //get if user is subscribed from this.user_info.userInfo$
    this.user_srv.userInfo$.pipe().subscribe(userInfo => {
      this._username = userInfo?.username || ''
    })
  }

  ngOnInit(): void {
    this._theme$ = this.store.select(getTheme)
    this._isMobile = window.innerWidth < 500
    this.showContentSub = this.traceStateService.showContent$.subscribe(
      ({ contentId, contentType }) => {
        this.contextNodeType = contentType as CanvasNodeType
        this.contextNodeContentId = contentId
        this.onShowNode()
      },
    )
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: any): void {
    this._isMobile = event.target.innerWidth < 1224
  }

  @HostListener('window:keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    if (event.code === 'Space' && !this.isPanning) {
      event.preventDefault()
      this.isPanning = true
      this.ngDiagramService.updateConfig({ viewportPanningEnabled: true })
    }
  }

  @HostListener('window:keyup', ['$event'])
  onKeyUp(event: KeyboardEvent): void {
    if (event.code === 'Space' && this.isPanning) {
      this.isPanning = false
      this.ngDiagramService.updateConfig({ viewportPanningEnabled: false })
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['canvasData'] && this.canvasData) {
      this.loadModel(this.canvasData)
    }
    if (changes['nodeResults']) {
      this.traceStateService.nodeResults.set(this.nodeResults)
      this.applyTraceStyles()
    }
    if (changes['isTraceMode'] && this.isEngineReady) {
      this.ngDiagramService.updateConfig({ nodeDraggingEnabled: !this.isTraceMode })
    }
  }

  ngOnDestroy(): void {
    this.cleanupEventListeners()
    this.showContentSub?.unsubscribe()
  }

  private loadModel(data: CanvasData): void {
    this.cleanupEventListeners()
    this.isLoadingModel = true

    const adapter = new HistoryModelAdapter()
    this.historyAdapter = adapter
    this.model = initializeModelAdapter(
      adapter,
      {
        nodes: data.nodes ?? [],
        edges: data.edges ?? [],
        metadata: data.metadata ?? {},
      },
      this.injector,
    )

    this.nodeCounter = (data.nodes?.length ?? 0) + 1
    if (this.isEngineReady) {
      // Delay so the library can settle the new model before we attach listeners
      setTimeout(() => {
        this.isLoadingModel = false
        this.registerChangeListeners()
        this.modelReady.emit()
      })
    }
    // if not ready yet, onDiagramInit() will handle it
  }

  onDiagramInit(): void {
    this.isEngineReady = true
    this.isLoadingModel = false
    this.registerChangeListeners()
    this.modelReady.emit()
  }

  private registerChangeListeners(): void {
    const saveSnapshot = (): void => this.historyAdapter?.saveSnapshot()
    const emitChange = (): void => {
      if (this.isLoadingModel) return
      this.diagramChanged.emit()
    }

    this.eventUnsubscribers = [
      // Save snapshot BEFORE the action so undo restores the prior state
      this.ngDiagramService.addEventListener('nodeDragStarted', saveSnapshot),
      this.ngDiagramService.addEventListener('nodeResizeStarted', saveSnapshot),
      this.ngDiagramService.addEventListener('nodeRotateStarted', saveSnapshot),
      // After action completes, mark canvas dirty
      this.ngDiagramService.addEventListener('nodeDragEnded', emitChange),
      this.ngDiagramService.addEventListener('selectionRemoved', () => {
        saveSnapshot()
        emitChange()
      }),
      this.ngDiagramService.addEventListener('edgeDrawEnded', () => {
        saveSnapshot()
        emitChange()
      }),
      this.ngDiagramService.addEventListener('nodeResized', emitChange),
      this.ngDiagramService.addEventListener('clipboardPasted', () => {
        saveSnapshot()
        emitChange()
      }),
      this.ngDiagramService.addEventListener('selectionRotated', emitChange),
      this.ngDiagramService.addEventListener('groupMembershipChanged', () => {
        saveSnapshot()
        emitChange()
      }),
    ]
  }

  private cleanupEventListeners(): void {
    this.eventUnsubscribers.forEach(unsub => unsub())
    this.eventUnsubscribers = []
  }

  getCurrentData(): CanvasData {
    return {
      nodes: this.modelService.nodes() as any[],
      edges: this.modelService.edges() as any[],
      metadata: this.modelService.metadata() as any,
    }
  }

  get canUndo(): boolean {
    return this.historyAdapter?.canUndo ?? false
  }

  get canRedo(): boolean {
    return this.historyAdapter?.canRedo ?? false
  }

  undo(): void {
    this.modelService.getModel()?.undo()
    this.diagramChanged.emit()
  }

  redo(): void {
    this.modelService.getModel()?.redo()
    this.diagramChanged.emit()
  }

  getModelJSON(): string {
    return this.modelService.toJSON()
  }

  getNodeStatusClass(nodeId: string): string {
    const result = this.nodeResults[nodeId]
    if (!result) return ''
    switch (result.status) {
      case 'success':
        return 'trace-node-success'
      case 'error':
        return 'trace-node-error'
      case 'running':
        return 'trace-node-running'
      default:
        return ''
    }
  }

  getEdgeStatusClass(edgeId: string): string {
    for (const nodeResult of Object.values(this.nodeResults)) {
      if (nodeResult.incoming_edges?.includes(edgeId)) {
        return nodeResult.status === 'success'
          ? 'trace-edge-success'
          : nodeResult.status === 'error'
            ? 'trace-edge-error'
            : ''
      }
    }
    return ''
  }

  onRunCanvas(): void {
    this.runCanvas.emit()
  }

  private applyTraceStyles(): void {
    // Use requestAnimationFrame to ensure DOM is ready
    requestAnimationFrame(() => {
      const container = this.el.nativeElement as HTMLElement

      // Clear previous trace classes from nodes
      container.querySelectorAll('.trace-success, .trace-error, .trace-running').forEach(el => {
        el.classList.remove('trace-success', 'trace-error', 'trace-running')
      })

      // Clear previous trace classes from edges
      container.querySelectorAll('.trace-edge-success, .trace-edge-error').forEach(el => {
        el.classList.remove('trace-edge-success', 'trace-edge-error')
      })

      if (!this.nodeResults || Object.keys(this.nodeResults).length === 0) return

      for (const [nodeId, result] of Object.entries(this.nodeResults)) {
        const nodeEl = container.querySelector(`[data-node-id="${nodeId}"]`)
        if (nodeEl) {
          const cls =
            result.status === 'success'
              ? 'trace-success'
              : result.status === 'error'
                ? 'trace-error'
                : result.status === 'running'
                  ? 'trace-running'
                  : ''
          if (cls) {
            nodeEl.classList.add(cls)
          }
        }

        // Apply trace classes to incoming edges
        if (result.incoming_edges) {
          const edgeCls =
            result.status === 'success'
              ? 'trace-edge-success'
              : result.status === 'error'
                ? 'trace-edge-error'
                : ''
          if (edgeCls) {
            for (const edgeId of result.incoming_edges) {
              const edgeEl = container.querySelector(`[data-edge-id="${edgeId}"]`)
              if (edgeEl) {
                edgeEl.classList.add(edgeCls)
              }
            }
          }
        }
      }
    })
  }

  onContextMenu(event: MouseEvent): void {
    event.preventDefault()
    this.contextMenuPosition = { x: event.clientX, y: event.clientY }

    // Check if right-click is on a node
    const nodeEl = (event.target as HTMLElement).closest('[data-node-type]')
    if (nodeEl) {
      this.contextNodeType = nodeEl.getAttribute('data-node-type') as CanvasNodeType
      this.contextNodeContentId = Number(nodeEl.getAttribute('data-content-id')) || null
      this.contextNodeId = nodeEl.getAttribute('data-node-id')
      this.nodeMenuTrigger.openMenu()
    } else {
      this.contextNodeType = null
      this.contextNodeContentId = null
      this.contextNodeId = null
      this.contextMenuTrigger.openMenu()
    }
  }

  onDoubleClick(event: MouseEvent): void {
    const target = event.target as HTMLElement
    const nodeEl = target.closest('[data-node-type]')
    if (!nodeEl) return

    const nodeType = nodeEl.getAttribute('data-node-type') as CanvasNodeType
    const nodeId = nodeEl.getAttribute('data-node-id')
    const contentId = Number(nodeEl.getAttribute('data-content-id')) || null

    // Check if double-click is on a configurable content area
    const configEl = target.closest('[data-configurable]')
    if (configEl) {
      const nodes = this.modelService.nodes() as any[]
      const matchedNode = nodeId
        ? nodes.find((n: any) => n.id === nodeId)
        : nodes.find((n: any) => n.type === nodeType)

      if (matchedNode) {
        this.openNodeConfigDialog(nodeType, matchedNode)
        return
      }
    }

    // For non-configurable nodes, open the read-only Show dialog if content exists
    if (
      contentId &&
      nodeType !== 'dictionary' &&
      nodeType !== 'create' &&
      nodeType !== 'attachment'
    ) {
      this.contextNodeType = nodeType
      this.contextNodeContentId = contentId
      this.onShowNode()
    }
  }

  private openNodeConfigDialog(nodeType: CanvasNodeType, node: any): void {
    let dialogData: NodeConfigDialogData | null = null

    if (nodeType === 'attachment') {
      dialogData = {
        title: 'Attachment Configuration',
        infoTooltip: 'Max 3 images can be selected',
        contentType: 'attachment',
        config: { images: [...(node.data?.images ?? [])] },
      }
    } else if (nodeType === 'create') {
      const subType = node.data?.create?.subType ?? 'dictionary'
      const defaultPrompt = `Create a ${subType}.`
      const config: Record<string, any> = {
        subType,
        prompt: node.data?.create?.prompt || defaultPrompt,
      }
      if (subType === 'figure') {
        config['figureStyle'] = node.data?.create?.figureStyle ?? 'bold'
      }
      dialogData = {
        title: `Create ${subType.charAt(0).toUpperCase() + subType.slice(1)} Configuration`,
        infoTooltip: 'Configure the prompt and options for this create node',
        contentType: 'create-node',
        config,
      }
    }

    if (!dialogData) return

    let dialogRef
    if (this._isMobile) {
      dialogRef = this.dialog.open(
        DdcMobileSheetComponent,
        this.getMobileDialogConfig('configure', dialogData),
      )
    } else {
      const config = new MatDialogConfig()
      config.width = '420px'
      config.maxWidth = '90vw'
      config.hasBackdrop = true
      config.data = { type: 'configure', element: dialogData }
      dialogRef = this.dialog.open(DdcDesktopDialogComponent, config)
    }

    dialogRef.afterClosed().subscribe((result: NodeConfigDialogResult | undefined) => {
      if (!result) return

      this.historyAdapter?.saveSnapshot()

      if (nodeType === 'attachment') {
        node.data = { ...node.data, images: result.config['images'] ?? [] }
      } else if (nodeType === 'create') {
        const updatedCreate: any = {
          ...node.data.create,
          prompt: result.config['prompt'] ?? node.data.create.prompt,
        }
        if (node.data?.create?.subType === 'figure') {
          updatedCreate.figureStyle = result.config['figureStyle'] ?? 'bold'
        }
        node.data = { ...node.data, create: updatedCreate }
      }

      // Replace the node with a new reference so OnPush components re-render
      this.historyAdapter?.updateNodes((nodes: any[]) =>
        nodes.map((n: any) => (n.id === node.id ? { ...node } : n)),
      )

      this.diagramChanged.emit()
    })
  }

  get canShowNode(): boolean {
    return (
      this.contextNodeType !== null &&
      this.contextNodeType !== 'dictionary' &&
      !!this.contextNodeContentId
    )
  }

  get canConfigureNode(): boolean {
    if (!this.contextNodeType) return false
    if (this.contextNodeType === 'attachment') return true
    if (this.contextNodeType === 'create') return true
    return false
  }

  onConfigureNode(): void {
    if (!this.contextNodeType) return
    const nodes = this.modelService.nodes() as any[]
    const node = this.contextNodeId
      ? nodes.find((n: any) => n.id === this.contextNodeId)
      : nodes.find((n: any) => n.type === this.contextNodeType)
    if (!node) return
    this.openNodeConfigDialog(this.contextNodeType, node)
  }

  onShowNode(): void {
    if (!this.contextNodeType || !this.contextNodeContentId) return
    const contentId = this.contextNodeContentId
    const type = this.contextNodeType

    // Dictionary has no read dialog — navigate to library page
    if (type === 'dictionary') {
      console.log('Navigating to dictionary with ID:', contentId)
      console.log('Username:', this._username)
      this.router.navigate(['/library/dic', contentId, this._username])
      return
    }

    const dialogTypeMap: Record<string, string> = {
      word: 'read-word',
      pair: 'read-pair',
      note: 'read-note',
      quiz: 'read-quiz',
      figure: 'read-figure',
    }

    const dialogType = dialogTypeMap[type]
    if (!dialogType) return

    const openDialog = (element: any): void => {
      if (this._isMobile) {
        this.dialog.open(DdcMobileSheetComponent, this.getMobileDialogConfig(dialogType, element))
      } else {
        const config = new MatDialogConfig()
        config.width = '600px'
        config.maxWidth = '90vw'
        config.hasBackdrop = true
        config.data = { type: dialogType, element }
        this.dialog.open(DdcDesktopDialogComponent, config)
      }
    }

    switch (type) {
      case 'word':
        this.wordService.get_word(contentId).subscribe((res: any) => openDialog(res.res))
        break
      case 'pair':
        this.pairService.get_pair(contentId).subscribe((res: any) => openDialog(res))
        break
      case 'note':
        this.noteService.get_note(contentId).subscribe((res: any) => openDialog(res.res))
        break
      case 'quiz':
        this.quizService.get_quiz(contentId).subscribe((res: any) => openDialog(res.res))
        break
      case 'figure':
        this.figureService.get_figure(contentId).subscribe((res: any) => openDialog(res.res))
        break
    }
  }

  private getMobileDialogConfig(type: string, element: any): MatDialogConfig {
    let themeClass = ''
    this._theme$
      .subscribe(theme => {
        themeClass = theme
      })
      .unsubscribe()
    const config = new MatDialogConfig()
    config.panelClass = ['mobile-sheet-container', themeClass]
    config.position = { bottom: '0' }
    config.width = '100%'
    config.height = '80vh'
    config.maxWidth = '100vw'
    config.hasBackdrop = true
    config.backdropClass = 'mobile-sheet-backdrop'
    config.enterAnimationDuration = '300ms'
    config.exitAnimationDuration = '200ms'
    config.data = { type, element }
    return config
  }

  onAddNode(): void {
    let themeClass = ''
    this._theme$
      .subscribe(theme => {
        themeClass = theme
      })
      .unsubscribe()

    const dialogRef = this.dialog.open(AddCanvasElementsDialogComponent, {
      width: '640px',
      height: '500px',
      panelClass: ['add-elements-dialog-panel', themeClass],
      autoFocus: true,
    })

    dialogRef.afterClosed().subscribe((result: AddCanvasElementResult | undefined) => {
      if (result?.type && result?.data) {
        this.addNodeWithData(result.type, result.data)
      }
    })
  }

  addNodeByType(type: string): void {
    const id = `node-${this.nodeCounter++}`
    const data = this.getDefaultDataForType(type)
    this.historyAdapter?.saveSnapshot()
    this.modelService.addNodes([
      {
        id,
        type,
        position: { x: 300 + Math.random() * 200, y: 200 + Math.random() * 200 },
        data,
      },
    ])
    this.diagramChanged.emit()
  }

  addNodeWithData(type: string, data: Record<string, any>): void {
    const id = `node-${type}-${this.nodeCounter++}`
    this.historyAdapter?.saveSnapshot()
    this.modelService.addNodes([
      {
        id,
        type,
        position: { x: 300 + Math.random() * 200, y: 200 + Math.random() * 200 },
        data,
      },
    ])
    this.diagramChanged.emit()
  }

  private getDefaultDataForType(type: string): Record<string, any> {
    switch (type) {
      case 'word':
        return { word: { id: 0, word: 'New Word', language: 'English' } }
      case 'pair':
        return {
          pair: {
            id: 0,
            word_1: { word: 'Word 1', language: 'Language 1' },
            word_2: { word: 'Word 2', language: 'Language 2' },
          },
        }
      case 'dictionary':
        return { dictionary: { id: 0, title: 'New Dictionary', words_count: 0, pairs_count: 0 } }
      case 'note':
        return { note: { id: 0, title: 'New Note', text: '' } }
      case 'quiz':
        return { quiz: { id: 0, title: 'New Quiz', questions_count: 0 } }
      case 'figure':
        return { figure: { id: 0, title: 'New Figure', figure_pic: '' } }
      case 'attachment':
        return { images: [] }
      default:
        return {}
    }
  }

  onZoomToFit(): void {
    // Placeholder - ngDiagram viewport service can be used here
  }

  onResetView(): void {
    // Placeholder - ngDiagram viewport service can be used here
  }

  onSelectAll(): void {
    const allNodeIds = (this.modelService.nodes() as any[]).map((n: any) => n.id)
    const allEdgeIds = (this.modelService.edges() as any[]).map((e: any) => e.id)
    this.selectionService.select(allNodeIds, allEdgeIds)
  }

  onClearCanvas(): void {
    const nodeIds = (this.modelService.nodes() as any[]).map((n: any) => n.id)
    const edgeIds = (this.modelService.edges() as any[]).map((e: any) => e.id)
    if (nodeIds.length > 0) this.modelService.deleteNodes(nodeIds)
    if (edgeIds.length > 0) this.modelService.deleteEdges(edgeIds)
    this.diagramChanged.emit()
  }
}
